import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Bell, Plus, Trash2, Check, Circle, Play, Volume2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

import { swallow } from "@/lib/log";
import { SOUND_OPTIONS, SOUND_PRESETS, playSound, getSoundLabel } from "@/lib/sounds";
import FavPinButtons from "@/components/FavPinButtons";
import RowActions from "@/components/RowActions";
import SwipeableRow from "@/components/SwipeableRow";

function fmt(dt) {
  if (!dt) return "No due time";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

export default function Reminders() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ text: "", due_at: "", recurrence: "none", sound: "chime" });
  const [query, setQuery] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/reminders"); setItems(data); } catch (e) { swallow("Reminders")(e); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ text: "", due_at: "", recurrence: "none", sound: "chime" }); setOpen(true); };

  // Contextual FAB deep-link: `?new=1` opens the new-reminder dialog
  // Optionally pre-fills text + due_at (used by Documents auto-reminder from OCR expiry)
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    if (sp.get("new") === "1") {
      const preText = sp.get("text") || "";
      const preDue = sp.get("due_at") || "";
      setForm({ text: preText, due_at: preDue, recurrence: "none", sound: "chime" });
      setOpen(true);
      const next = new URLSearchParams(sp);
      next.delete("new"); next.delete("text"); next.delete("due_at");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      const hay = [
        r.text || "",
        r.recurrence || "",
        r.sound || "",
        r.due_at ? new Date(r.due_at).toLocaleString().toLowerCase() : "",
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const save = async () => {
    if (!form.text.trim()) { toast.error("Reminder text required"); return; }
    const payload = { text: form.text, recurrence: form.recurrence, sound: form.sound };
    if (form.due_at) payload.due_at = new Date(form.due_at).toISOString();
    try {
      const { data: saved } = await api.post("/reminders", payload);
      // Optimistic add — surface the new reminder immediately even if the SW
      // read cache briefly returns a stale /api/reminders snapshot.
      if (saved && saved.id) {
        setItems((prev) => (prev.some((x) => x.id === saved.id) ? prev : [saved, ...prev]));
      }
      setOpen(false);
      toast.success("Reminder set");
      load(); // background reconciliation
    } catch { toast.error("Failed to save"); }
  };

  const toggleDone = async (r) => {
    // Optimistic flip so the tick reflects instantly regardless of SW cache.
    setItems((arr) => arr.map((x) => x.id === r.id ? { ...x, done: !x.done } : x));
    try { await api.put(`/reminders/${r.id}`, { done: !r.done }); load(); }
    catch { toast.error("Failed"); load(); }
  };
  // RowActions renders the delete-with-undo toast; here we just do the API + UI update.
  const del = async (r) => {
    setItems((arr) => arr.filter((it) => it.id !== r.id));
    try { await api.delete(`/reminders/${r.id}`); }
    catch { toast.error("Delete failed — restored"); load(); }
  };

  const upcoming = filtered.filter((r) => !r.done);
  const past = filtered.filter((r) => r.done);

  return (
    <>
      <TopBar title="Reminders" subtitle="Never miss what matters"
        showHome={false}
        right={
          <button onClick={openNew} className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid="reminders-add-button">
            <Plus className="h-4 w-4" />
          </button>
        }
      />
      <div className="px-4 py-4 space-y-4">
        {items.length > 0 && (
          <div className="relative" data-testid="reminders-search-bar">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reminders…"
              className="pl-9 pr-9"
              data-testid="reminders-search-input"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center"
                aria-label="Clear search"
                data-testid="reminders-search-clear"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        )}
        {items.length === 0 && (
          <EmptyState icon={Bell} title="No reminders yet" subtitle="Try: 'Remind me every Sunday to call my parents.'" />
        )}
        {items.length > 0 && filtered.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-8" data-testid="reminders-no-results">
            No reminders match “{query}”.
          </div>
        )}
        {upcoming.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Upcoming</div>
            <div className="space-y-2">
              {upcoming.map((r) => <ReminderRow key={r.id} r={r} onToggle={toggleDone} onDelete={del} onChanged={(u) => setItems((arr) => arr.map((x) => x.id === r.id ? { ...x, ...u } : x))} onArchived={(id) => setItems((arr) => arr.filter((x) => x.id !== id))} />)}
            </div>
          </div>
        )}
        {past.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Done</div>
            <div className="space-y-2">
              {past.map((r) => <ReminderRow key={r.id} r={r} onToggle={toggleDone} onDelete={del} onChanged={(u) => setItems((arr) => arr.map((x) => x.id === r.id ? { ...x, ...u } : x))} onArchived={(id) => setItems((arr) => arr.filter((x) => x.id !== id))} />)}
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">New reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reminder</Label>
              <Input value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Pay LIC premium" data-testid="reminder-form-text" />
            </div>
            <div>
              <Label className="text-xs">When</Label>
              <Input type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} data-testid="reminder-form-datetime" />
            </div>
            <div>
              <Label className="text-xs">Repeat</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={form.recurrence}
                onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                data-testid="reminder-form-recurrence"
              >
                {["none","daily","weekly","monthly","yearly"].map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Volume2 className="h-3 w-3" /> Sound</Label>
              <div className="grid grid-cols-2 gap-2 mt-1" data-testid="reminder-form-sound-group">
                {SOUND_OPTIONS.map((id) => {
                  const p = SOUND_PRESETS[id];
                  const selected = form.sound === id;
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => setForm({ ...form, sound: id })}
                      className={`rounded-xl border p-2.5 text-left transition ${
                        selected
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border bg-card hover:bg-[hsl(var(--accent))]"
                      }`}
                      data-testid={`reminder-sound-option-${id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{p.label}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{p.description}</div>
                        </div>
                        {id !== "none" && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); playSound(id); }}
                            className="h-7 w-7 shrink-0 rounded-full border border-border bg-background flex items-center justify-center hover:bg-muted"
                            data-testid={`reminder-sound-preview-${id}`}
                            aria-label={`Preview ${p.label}`}
                          >
                            <Play className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Plays inside Impo when the reminder is due.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="reminder-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ReminderRow({ r, onToggle, onDelete, onChanged, onArchived }) {
  const swipeLeft = async () => {
    onArchived?.(r.id);
    try { await api.post("/archive/toggle", { type: "reminder", id: r.id, archived: true }); toast.success("Reminder archived"); }
    catch { toast.error("Archive failed"); onChanged?.({ is_archived: false }); }
  };
  const swipeRight = async () => {
    const next = !r.is_favorite;
    onChanged?.({ is_favorite: next });
    try { await api.post("/favorites/toggle", { type: "reminder", id: r.id, is_favorite: next }); toast.success(next ? "Added to Favorites" : "Removed from Favorites"); }
    catch { toast.error("Update failed"); onChanged?.({ is_favorite: !next }); }
  };
  return (
    <SwipeableRow testId={`swipe-reminder-${r.id}`} onSwipeLeft={swipeLeft} onSwipeRight={swipeRight}>
      <div className="rounded-2xl border border-border bg-card p-3 flex items-start gap-3" data-testid="reminder-item">
      <button onClick={() => onToggle(r)} className={`h-8 w-8 rounded-full flex items-center justify-center border ${r.done ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} aria-label="toggle done" data-testid="reminder-toggle-done">
        {r.done ? <Check className="h-4 w-4" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${r.done ? "line-through text-muted-foreground" : ""}`}>{r.text}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
          <span>{fmt(r.due_at)}</span>
          {r.recurrence && r.recurrence !== "none" && <Badge className="rounded-full text-[10px]">{r.recurrence}</Badge>}
          {r.sound && r.sound !== "none" && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); playSound(r.sound); }}
              className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] hover:bg-muted"
              data-testid="reminder-sound-play"
              aria-label={`Play ${r.sound} sound`}
            >
              <Volume2 className="h-2.5 w-2.5" /> {getSoundLabel(r.sound)}
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <FavPinButtons
          type="reminder"
          id={r.id}
          isFavorite={!!r.is_favorite}
          pinnedAt={r.pinned_at}
          size="sm"
          onChanged={onChanged}
          onArchived={onArchived}
        />
        <RowActions
          size="sm"
          testId={`reminder-${r.id}`}
          deleteLabel="Delete reminder"
          onDelete={() => onDelete(r)}
        />
      </div>
      </div>
    </SwipeableRow>
  );
}
